import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { Setting } from '../classes/setting';
import { ScholarshipTable } from '../classes/scholarship-table';
import { School } from '../classes/school';
import { EditSchools } from '../classes/edit-schools';
import { ScholarshipDetails } from '../classes/scholarship-details';
import { AddScholarship } from '../classes/add-scholarship';
import { EditGeneral } from '../classes/edit-general';
import { EditContacts } from '../classes/edit-contacts';
import { EditAdditionals } from '../classes/edit-additionals';
import { AppSubmission } from '../classes/application-submission';
import { AppStatusTable } from '../classes/application-status-table';
import { AppTable } from '../classes/application-table';
import { AppDetails } from '../classes/application-details';
import { Disqualify } from '../classes/disqualify';
import { ScholarshipManagerTable } from '../classes/scholarship-manager-table';
import { Award } from '../classes/award';
import { AddStudent } from '../classes/add-student';
import { RollForwardTable } from '../classes/roll-forward-table';
import { RollForward } from '../classes/roll-forward';
import { Student } from '../classes/student';
import { Reopen } from '../classes/reopen';
import { StudentApp } from '../classes/student-application';
import { AppStudent } from '../classes/app-student';
import { AppFinancial } from '../classes/app-financial';
import { AppFamily } from '../classes/app-family';
import { AppCollege } from '../classes/app-college';
import { AppEssay } from '../classes/app-essay';
import { AppSelection } from '../classes/app-selection';
import { AppFinalize } from '../classes/app-finalize';
import { UpdateFocus } from '../classes/update-focus';
import { Instructions } from '../classes/instructions';
import { EditRequirements } from '../classes/edit-requirements';
import { ScholarshipDescription } from '../classes/scholarship-description';
import { PostUploadReturn } from '../classes/post-upload-return';
import { UserInfo } from './user.service';
import { Primitive } from '../classes/primitive';
import { StudentRegister } from '../classes/student-register';
import { AuthPacket, PasswordResetRequestPacket, PasswordResetTokenStatus, UserAuth } from './auth.service';
import { getBaseUrl } from '../app/app.config';
import { AppSignature } from '../classes/app-signature';

@Injectable({ providedIn: 'root' })
export class AppService {
    protected readonly http = inject(HttpClient);

    protected handleError(error: HttpErrorResponse) {
        if (error.error != null) {
            let customError: HttpErrorResponse;

            if (typeof error.error === 'string' && error.error.includes('Session has expired')) {
                customError = new HttpErrorResponse({
                    error: 'Session has expired',
                    headers: error.headers,
                    status: error.status,
                    statusText: error.statusText,
                    url: error.url || undefined
                });

                return throwError(() => customError);
            }
            else {
                return throwError(() => error);
            }
        }

        let messageError = new HttpErrorResponse({
            error: error.message,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url || undefined
        });

        return throwError(() => messageError);
    }

    GetSettings(): Observable<Setting[]> {
        return this.http
            .get<Setting[]>(getBaseUrl() + `api/App/GetSettings`)
            .pipe(catchError(this.handleError));
    }

    GetScholarshipTable(): Observable<ScholarshipTable[]> {
        return this.http
            .get<ScholarshipTable[]>(
                getBaseUrl() +
                `api/App/GetScholarshipTable`
            )
            .pipe(catchError(this.handleError));
    }

    GetSchools(): Observable<School[]> {
        return this.http
            .get<School[]>(
                getBaseUrl() +
                `api/App/GetSchools`
            )
            .pipe(catchError(this.handleError));
    }

    AddScholarship(add: AddScholarship): Observable<AddScholarship> {
        return this.http
            .post<AddScholarship>(
                getBaseUrl() + `api/App/AddScholarship`,
                add
            )
            .pipe(catchError(this.handleError));
    }

    EditRequirements(edit: EditRequirements): Observable<EditRequirements> {
        return this.http
            .post<EditRequirements>(
                getBaseUrl() + `api/App/EditRequirements`,
                edit
            )
            .pipe(catchError(this.handleError));
    }

    EditGeneral(edit: EditGeneral): Observable<EditGeneral> {
        return this.http
            .post<EditGeneral>(
                getBaseUrl() + `api/App/EditGeneral`,
                edit
            )
            .pipe(catchError(this.handleError));
    }

    EditContacts(edit: EditContacts): Observable<EditContacts> {
        return this.http
            .post<EditContacts>(
                getBaseUrl() + `api/App/EditContacts`,
                edit
            )
            .pipe(catchError(this.handleError));
    }

    EditSchools(wrapper: EditSchools): Observable<EditSchools> {
        return this.http
            .post<EditSchools>(
                getBaseUrl() + `api/App/EditSchools`,
                wrapper
            )
            .pipe(catchError(this.handleError));
    }

    EditAdditionals(edit: EditAdditionals): Observable<EditAdditionals> {
        return this.http
            .post<EditAdditionals>(
                getBaseUrl() + `api/App/EditAdditionals`,
                edit
            )
            .pipe(catchError(this.handleError));
    }

    GetScholarshipDetails(
        masterID: number
    ): Observable<ScholarshipDetails> {
        return this.http
            .get<ScholarshipDetails>(
                getBaseUrl() +
                `api/App/GetScholarshipDetails?MasterID=${masterID}`
            )
            .pipe(catchError(this.handleError));
    }

    GetSubmittedTable(
        submitted: boolean
    ): Observable<AppSubmission> {
        return this.http
            .get<AppSubmission>(
                getBaseUrl() +
                `api/App/GetSubmittedTable?Submitted=${submitted}`
            )
            .pipe(catchError(this.handleError));
    }

    GetStatusTable(): Observable<AppStatusTable[]> {
        return this.http
            .get<AppStatusTable[]>(
                getBaseUrl() +
                `api/App/GetStatusTable`
            )
            .pipe(catchError(this.handleError));
    }

    GetApplicationsTable(): Observable<AppTable[]> {
        return this.http
            .get<AppTable[]>(
                getBaseUrl() +
                `api/App/GetApplicationsTable`
            )
            .pipe(catchError(this.handleError));
    }

    GetApplicationDetails(
        training: boolean,
        appID: number
    ): Observable<AppDetails> {
        return this.http
            .get<AppDetails>(
                getBaseUrl() +
                `api/App/GetApplicationDetails?Training=${training}&AppID=${appID}`
            )
            .pipe(catchError(this.handleError));
    }

    GetSelectedScholarshipDescriptions(
        appID: number
    ): Observable<ScholarshipDescription[]> {
        return this.http
            .get<ScholarshipDescription[]>(
                getBaseUrl() +
                `api/App/GetSelectedScholarshipDescriptions?AppID=${appID}`
            )
            .pipe(catchError(this.handleError));
    }

    Disqualify(dto: Disqualify): Observable<AppTable[]> {
        return this.http
            .post<AppTable[]>(
                getBaseUrl() + `api/App/Disqualify`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    GetScholarshipManagerTable(
        scholarshipID: number
    ): Observable<ScholarshipManagerTable> {
        return this.http
            .get<ScholarshipManagerTable>(
                getBaseUrl() +
                `api/App/GetScholarshipManagerTable?ScholarshipID=${scholarshipID}`
            )
            .pipe(catchError(this.handleError));
    }

    AwardScholarship(dto: Award): Observable<ScholarshipManagerTable> {
        return this.http
            .post<ScholarshipManagerTable>(
                getBaseUrl() + `api/App/AwardScholarship`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    AddStudent(dto: AddStudent): Observable<ScholarshipManagerTable> {
        return this.http
            .post<ScholarshipManagerTable>(
                getBaseUrl() + `api/App/AddStudent`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    GetAvailableStudents(
        scholarshipID: number
    ): Observable<string[]> {
        return this.http
            .get<string[]>(
                getBaseUrl() +
                `api/App/GetAvailableStudents?ScholarshipID=${scholarshipID}`
            )
            .pipe(catchError(this.handleError));
    }

    GetFinalistDetails(
        scholarshipID: number
    ): Observable<AppDetails[]> {
        return this.http
            .get<AppDetails[]>(
                getBaseUrl() +
                `api/App/GetFinalistDetails?ScholarshipID=${scholarshipID}`
            )
            .pipe(catchError(this.handleError));
    }

    SendReminderEmails(): Observable<string> {
        return this.http
            .get<string>(
                getBaseUrl() +
                `api/App/SendReminderEmails`
            )
            .pipe(catchError(this.handleError));
    }

    GetRollForwardTable(): Observable<RollForwardTable[]> {
        return this.http
            .get<RollForwardTable[]>(
                getBaseUrl() +
                `api/App/GetRollForwardTable`
            )
            .pipe(catchError(this.handleError));
    }

    RollForward(dto: RollForward): Observable<RollForwardTable[]> {
        return this.http
            .post<RollForwardTable[]>(
                getBaseUrl() + `api/App/RollForward`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SearchStudent(studentID: string): Observable<Student> {
        return this.http
            .get<Student>(
                getBaseUrl() +
                `api/App/SearchStudent?StudentID=${studentID}`
            )
            .pipe(catchError(this.handleError));
    }

    ReopenApplication(dto: Reopen): Observable<Reopen> {
        return this.http
            .post<Reopen>(
                getBaseUrl() + `api/App/ReopenApplication`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    GetStudentApplication(
        studentID: string,
        training: boolean
    ): Observable<StudentApp> {
        return this.http
            .get<StudentApp>(
                getBaseUrl() +
                `api/App/GetStudentApplication?StudentID=${studentID}&Training=${training}`
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppStudent(dto: AppStudent): Observable<AppStudent> {
        return this.http
            .post<AppStudent>(
                getBaseUrl() + `api/App/SaveAppStudent`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppFinancial(dto: AppFinancial): Observable<AppFinancial> {
        return this.http
            .post<AppFinancial>(getBaseUrl() + `api/App/SaveAppFinancial`, dto)
            .pipe(catchError(this.handleError));
    }

    SaveAppFamily(dto: AppFamily): Observable<AppFamily> {
        return this.http
            .post<AppFamily>(
                getBaseUrl() + `api/App/SaveAppFamily`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppCollege(dto: AppCollege): Observable<AppCollege> {
        return this.http
            .post<AppCollege>(
                getBaseUrl() + `api/App/SaveAppCollege`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppEssay(dto: AppEssay): Observable<AppEssay> {
        return this.http
            .post<AppEssay>(
                getBaseUrl() + `api/App/SaveAppEssay`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppSelection(dto: AppSelection): Observable<AppSelection> {
        return this.http
            .post<AppSelection>(
                getBaseUrl() + `api/App/SaveAppSelection`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    SaveAppSignature(dto: AppSignature): Observable<AppSignature> {
        return this.http
            .post<AppSignature>(getBaseUrl() + `api/App/SaveAppSignature`, dto)
            .pipe(catchError(this.handleError));
    }

    FinalizeApp(dto: AppFinalize): Observable<AppFinalize> {
        return this.http
            .post<AppFinalize>(
                getBaseUrl() + `api/App/FinalizeApp`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    GetFocusData(dto: UpdateFocus): Observable<UpdateFocus> {
        return this.http
            .post<UpdateFocus>(
                getBaseUrl() + `api/App/GetFocusData`,
                dto
            )
            .pipe(catchError(this.handleError));
    }

    ReadInstructions(dto: Instructions): Observable<Date | null> {
        return this.http
            .post<Date | null>(
                getBaseUrl() + `api/App/ReadInstructions`,
                dto
            )
            .pipe(
                catchError(this.handleError),
                map((data) => {
                    return data ? new Date(data) : null;
                })
            );
    }

    ScholarshipDetailsReport(): Observable<HttpResponse<Blob>> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'PefScholarships',
        });

        headers = headers.set(
            'Accept',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );

        return this.http
            .get(
                getBaseUrl() +
                `api/Download/ScholarshipDetailsReport`,
                { headers: headers, observe: 'response', responseType: 'blob' }
            )
            .pipe(catchError(this.handleError));
    }

    ApplicationStatusReport(
        school: string
    ): Observable<HttpResponse<Blob>> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'PefScholarships',
        });

        headers = headers.set(
            'Accept',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );

        return this.http
            .get(
                getBaseUrl() +
                `api/Download/ApplicationStatusReport?SchoolID=${school}`,
                { headers: headers, observe: 'response', responseType: 'blob' }
            )
            .pipe(catchError(this.handleError));
    }

    ApplicationDetailsReport(
        training: boolean,
        applicationID: number
    ): Observable<HttpResponse<Blob>> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'PefScholarships',
        });

        headers = headers.set('Accept', 'application/pdf');

        return this.http
            .get(
                getBaseUrl() +
                `api/Download/ApplicationDetailsReport?Training=${training}&AppID=${applicationID}`,
                { headers: headers, observe: 'response', responseType: 'blob' }
            )
            .pipe(catchError(this.handleError));
    }

    FinalistDetailsReport(
        scholarshipID: number
    ): Observable<HttpResponse<Blob>> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'PefScholarships',
        });

        headers = headers.set('Accept', 'application/octet-stream');

        return this.http
            .get(
                getBaseUrl() +
                `api/Download/FinalistDetailsReport?ScholarshipID=${scholarshipID}`,
                { headers: headers, observe: 'response', responseType: 'blob' }
            )
            .pipe(catchError(this.handleError));
    }

    CompletedApplicationsReport(): Observable<HttpResponse<Blob>> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'PefScholarships',
        });

        headers = headers.set(
            'Accept',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );

        return this.http
            .get(
                getBaseUrl() +
                `api/Download/CompletedApplicationsReport`,
                { headers: headers, observe: 'response', responseType: 'blob' }
            )
            .pipe(catchError(this.handleError));
    }

    UploadPostSpreadsheet(dto: FormData): Observable<PostUploadReturn> {
        return this.http.post<PostUploadReturn>(getBaseUrl() + `api/Download/UploadPostSpreadsheet`, dto)
            .pipe(catchError(this.handleError));
    }

    ClasslinkSSOLogin(token: string, screen?: string, winres?: string) {
        return this.http.post<UserInfo>(
            getBaseUrl() + 'api/Auth/classlink',
            {
                Token: token,
                Screen: screen,
                Window: winres,
            }
        );
    }

    public PolkSSOLogin = (
        authToken: string,
        screen?: string,
        winres?: string
    ): Observable<UserInfo> => {
        return this.http
            .post<UserInfo>(getBaseUrl() + 'api/Auth/PolkSSOLogin', {
                EncryptedAuthToken: authToken,
                Screen: screen,
                Window: winres,
            })
            .pipe(catchError(this.handleError));
    };

    refreshAuthTokenCall(AuthToken: string): Observable<AuthPacket> {
        return this.http
            .get<AuthPacket>(
                getBaseUrl() +
                `api/Auth/RefreshAuthToken?AuthToken=${AuthToken || ''}`
            )
            .pipe(catchError(this.handleError));
    }

    ResetPassword(
        token: string | null | undefined,
        password: string | null | undefined
    ) {
        return this.http
            .post<PasswordResetTokenStatus>(
                getBaseUrl() + `api/Auth/password-reset`,
                {
                    Token: token,
                    Password: password,
                }
            )
            .pipe(catchError(this.handleError));
    }

    SubmitPasswordResetRequest(
        packet: PasswordResetRequestPacket
    ): Observable<Date | null> {
        return this.http
            .post<string>(
                getBaseUrl() + `api/Auth/password-request`,
                packet
            )
            .pipe(
                catchError(this.handleError),
                map((data) => {
                    return data ? new Date(data) : null;
                })
            );
    }

    checkAuthToken(AuthToken: string): Observable<UserAuth> {
        return this.http
            .get<UserAuth>(
                getBaseUrl() +
                `api/Auth/checkAuthToken?AuthToken=${AuthToken || ''}`
            )
            .pipe(catchError(this.handleError));
    }

    public ValidateImpersonatedUser = (
        username: string,
        password: string,
        impersonatedUsername?: string,
        trainingMode?: boolean,
        screen?: string,
        winres?: string
    ): Observable<UserInfo> => {
        return this.http
            .post<UserInfo>(getBaseUrl() + 'api/Auth/ValidateUser', {
                Username: username,
                Password: password,
                ImpersonatedUsername: impersonatedUsername,
                ITraining: trainingMode,
                ImpToken: 'D53101E1-5692-459F-81AB-DB8BBF9F06E4',
                Screen: screen,
                Window: winres,
            })
            .pipe(catchError(this.handleError));
    };

    ValidatePasswordResetToken(token: string) {
        return this.http
            .post<PasswordResetTokenStatus>(
                getBaseUrl() + `api/Auth/password-token`,
                {
                    Token: token,
                }
            )
            .pipe(catchError(this.handleError));
    }

    public ValidateUser(
        username: string,
        password: string,
        impersonationSelection?: string,
        clearTraining?: boolean,
        impersonatedUsername?: string
    ): Observable<UserInfo> {
        return this.http
            .post<UserInfo>(getBaseUrl() + 'api/Auth/ValidateUser', {
                Username: username,
                Password: password,
                ImpersonationSelection: impersonationSelection,
                ClearTraining: clearTraining,
                ImpersonatedUsername: impersonatedUsername,
            })
            .pipe(catchError(this.handleError));
    }

    RegisterStudent(dto: StudentRegister): Observable<Primitive> {
        return this.http
            .post<Primitive>(
                getBaseUrl() + `api/Auth/RegisterStudent`,
                dto
            )
            .pipe(catchError(this.handleError));
    }


}
